// @need-translate
import throttle from 'lodash/throttle';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {requestContext} from '../../../../../../common/requestContext';
import {preventDefaultEvent} from '../../../../../../common/shared/utils/preventDefaultEvent.function';
import {twMerge} from '../../../../../../common/twMerge';
import useTypedSelector from '../../../../../../common/useTypedSelector';
import {IconType} from '../../../../../../design-system/atoms/Icon/IconType.enum';
import {getInternalAssetImage} from '../../../../../../design-system/atoms/StaticImage/getInternalAssetImage.function';
import {STATIC_IMAGE_INFO_MAP} from '../../../../../../design-system/atoms/StaticImage/StaticImageInfoMap.constant';
import {Text} from '../../../../../../design-system/atoms/Text/Text';
import useScrollViewerWheelEvent from '../hook/useScrollViewerWheelEvent';
import {useViewerMenuHandler} from '../hook/useViewerMenuHandler';

export function ScrollViewerNonStopWatchingBottomContainer() {
  const {isMobile} = requestContext.requestInfo.display;
  const touchStartPositisionY = useRef(0);
  const [reachedEnd, setReachedEnd] = useState(false);
  const {handleMoveNextProduct} = useViewerMenuHandler({isMobile});
  const reachedEndRef = useRef(false);
  const movedBottomRef = useRef(false);

  const onReleaseHandler = useCallback(() => {
    if (!reachedEnd) {
      return;
    }

    if (movedBottomRef.current) {
      movedBottomRef.current = false;
      handleMoveNextProduct();
    }
  }, [handleMoveNextProduct, reachedEnd]);

  const onMoveHandler = useCallback(
    (event: TouchEvent) => {
      if (!reachedEnd) {
        return;
      }

      const touchMovePositisionY = event.changedTouches[0].clientY;
      if (
        !movedBottomRef.current &&
        touchStartPositisionY.current > touchMovePositisionY
      ) {
        movedBottomRef.current = true;
        handleMoveNextProduct();
      }
    },
    [handleMoveNextProduct, reachedEnd]
  );

  const onPressHandler = useCallback((event: TouchEvent) => {
    touchStartPositisionY.current = event.touches[0].clientY;
  }, []);

  const onWheelHandler = useMemo(
    () =>
      throttle(
        (event: WheelEvent) => {
          if (!reachedEnd) {
            return;
          }

          if (event.deltaY > 0) {
            handleMoveNextProduct();
          }
        },
        500,
        {
          leading: true,
          trailing: false,
        }
      ),
    [handleMoveNextProduct, reachedEnd]
  );

  const onKeyDownHandler = useCallback(
    (event: KeyboardEvent) => {
      if (!reachedEnd) {
        return;
      }

      if (event.key === 'ArrowDown') {
        handleMoveNextProduct();
      }
    },
    [handleMoveNextProduct, reachedEnd]
  );

  const handleScroll = useMemo(
    () =>
      throttle(() => {
        // 스크롤이 가장 아래로 내려온 경우, reachedEnd 값을 true로 변경합니다.
        if (
          window.innerHeight + Math.ceil(window.pageYOffset + 1) >=
          document.body.scrollHeight
        ) {
          if (!reachedEndRef.current) {
            setReachedEnd(true);
          }
        } else {
          if (reachedEndRef.current && !movedBottomRef.current) {
            setReachedEnd(false);
          }
        }
      }, 100),
    []
  );

  useEffect(() => {
    reachedEndRef.current = reachedEnd;
  }, [reachedEnd]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  useScrollViewerWheelEvent({
    onPressHandler,
    onMoveHandler,
    onReleaseHandler,
    onScroll: handleScroll,
    onWheelHandler,
    onKeyDownHandler,
    isReached: reachedEnd,
    isReachedRef: reachedEndRef,
  });

  const isBlackSpaceColor = useTypedSelector(
    state => state.imageViewer.settings.spaceColor === 'Black'
  );
  const imageInfo = STATIC_IMAGE_INFO_MAP[IconType.ListArrowDown];

  return (
    <div
      className={twMerge(
        'flex w-full justify-center',
        isMobile ? 'h-20vh items-end' : 'h-192pxr items-center'
      )}
    >
      <div
        className={twMerge(
          'flex flex-row items-center justify-center py-2pxr pl-24pxr',
          isMobile && 'mb-32pxr'
        )}
      >
        {/* 여백 위에 존재하는 텍스트, 아이콘이라 다크모드에 영향을 안받습니다. 예외로 처리합니다. */}
        <Text
          className={twMerge(
            'font-medium2',
            isBlackSpaceColor ? 'text-static-mono-10' : 'text-static-mono-90'
          )}
        >
          스크롤 시 다음화로 이동
        </Text>
        <>
          {isBlackSpaceColor ? (
            <img
              alt={imageInfo.alt}
              src={getInternalAssetImage(imageInfo.srcDark ?? '')}
              onSelect={preventDefaultEvent}
              onDragStart={preventDefaultEvent}
              onContextMenu={preventDefaultEvent}
            />
          ) : (
            <img
              alt={imageInfo.alt}
              src={getInternalAssetImage(imageInfo.srcLight ?? '')}
              onSelect={preventDefaultEvent}
              onDragStart={preventDefaultEvent}
              onContextMenu={preventDefaultEvent}
            />
          )}
        </>
      </div>
    </div>
  );
}
